import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import {FormArray, FormGroup} from "@angular/forms";
import {
  JaarrapportageEntity, ClientEntity,
  TrialBalanceItem, Jaarrekening, Jaarrapportage, BokkzJrLogic
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { combineLatest, distinctUntilChanged, Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SpecTrialbalanceComponent } from '../spec-trialbalance/spec-trialbalance.component';
import { SpecFormattedexplanationComponent } from '../spec-formattedexplanation/spec-formattedexplanation.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-liquide-middelen',
  templateUrl: './spec-liquide-middelen.component.html',
  imports: [
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SharedUiFormControlsModule,
    SpecTrialbalanceComponent,
    SpecFormattedexplanationComponent,
    CommonModule
  ],
  styleUrls: ['./spec-liquide-middelen.component.scss']
})
export class SpecLiquideMiddelenComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();

  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public client = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;

  public cashAndCashEquivalentsFg = {} as FormGroup;
  public cashInHandFg = {} as FormGroup;
  public creditBalancesOnBankAccountsFg = {} as FormGroup;
  public billsExchangeChequesFg = {} as FormGroup;
  public cashAndCashEquivalentsDisclosureFg = {} as FormGroup;
  public billsExchangeChequesDisclosureFg = {} as FormGroup;
  public cashAndCashEquivalentsUnavailableExplanationFg = {} as FormGroup;
  public cashAndCashEquivalentsNoLegalOwnershipDescriptionFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    private cdRef: ChangeDetectorRef,
    private jrLogicService: BokkzJrLogic,
    public jrDataService: BokkzJrDataService) {
    this.createFormGroups();
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createFormGroups() {
    this.cashAndCashEquivalentsFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.cashInHandFg = this.jrDataService.createFormGroupBalanceItem();
    this.creditBalancesOnBankAccountsFg = this.jrDataService.createFormGroupBalanceItem();
    this.billsExchangeChequesFg = this.jrDataService.createFormGroupBalanceItem();
    this.cashAndCashEquivalentsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.billsExchangeChequesDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.cashAndCashEquivalentsUnavailableExplanationFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.cashAndCashEquivalentsNoLegalOwnershipDescriptionFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  private patchData(value: JaarrapportageEntity) {
    const liquideMiddelen = value.rapportage?.jaarrekeningen?.at(0)?.balans?.liquideMiddelen;

    if (liquideMiddelen) {
      this.cashAndCashEquivalentsFg.reset();

      if (liquideMiddelen.cashAndCashEquivalents) {
        this.cashAndCashEquivalentsFg.patchValue(liquideMiddelen.cashAndCashEquivalents, {emitEvent: false})
      }

      const uitsplitsing = liquideMiddelen.liquideMiddelenUitsplitsing;
      const tekstueel = liquideMiddelen.liquideMiddelenTekstueel;

      if (uitsplitsing) {
        if (uitsplitsing.cashInHand) {
          const subItem = uitsplitsing.cashInHand;
          this.cashInHandFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.cashInHandFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing.creditBalancesOnBankAccounts) {
          const subItem = uitsplitsing.creditBalancesOnBankAccounts;
          this.creditBalancesOnBankAccountsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.creditBalancesOnBankAccountsFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing.billsExchangeCheques) {
          const subItem = uitsplitsing.billsExchangeCheques;
          this.billsExchangeChequesFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.billsExchangeChequesFg.get('trialBalanceItems') as FormArray)
        }
      }
      if (tekstueel) {
        if (tekstueel.cashAndCashEquivalentsDisclosure) {
          this.cashAndCashEquivalentsDisclosureFg.patchValue(tekstueel.cashAndCashEquivalentsDisclosure);
        }
        if (tekstueel.billsExchangeChequesDisclosure) {
          this.billsExchangeChequesDisclosureFg.patchValue(tekstueel.billsExchangeChequesDisclosure);
        }
        if (tekstueel.cashAndCashEquivalentsUnavailableExplanation) {
          this.cashAndCashEquivalentsUnavailableExplanationFg.patchValue(tekstueel.cashAndCashEquivalentsUnavailableExplanation);
        }
        if (tekstueel.cashAndCashEquivalentsNoLegalOwnershipDescription) {
          this.cashAndCashEquivalentsNoLegalOwnershipDescriptionFg.patchValue(tekstueel.cashAndCashEquivalentsNoLegalOwnershipDescription);
        }
      }
    }
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i]);
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i])
          formArray.push(formGroup);
        }
      }
    }
  }

  saveForm(): void {
    this.showCustomControls = false;
    this.cdRef.detectChanges();
    this.cashAndCashEquivalentsFg.updateValueAndValidity();
    this.cashInHandFg.updateValueAndValidity();
    this.creditBalancesOnBankAccountsFg.updateValueAndValidity();
    this.billsExchangeChequesFg.updateValueAndValidity();
    this.cashAndCashEquivalentsDisclosureFg.updateValueAndValidity();
    this.billsExchangeChequesDisclosureFg.updateValueAndValidity();
    this.cashAndCashEquivalentsUnavailableExplanationFg.updateValueAndValidity();
    this.cashAndCashEquivalentsNoLegalOwnershipDescriptionFg.updateValueAndValidity();

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        balans: {
          ...this.jrEntity.rapportage.jaarrekeningen[0].balans,
          liquideMiddelen: {
            ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.liquideMiddelen,
            liquideMiddelenUitsplitsing: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.liquideMiddelen?.liquideMiddelenUitsplitsing,
              cashInHand: this.cashInHandFg.value,
              creditBalancesOnBankAccounts: this.creditBalancesOnBankAccountsFg.value,
              billsExchangeCheques: this.billsExchangeChequesFg.value,
            },
            liquideMiddelenTekstueel: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.liquideMiddelen?.liquideMiddelenTekstueel,
              cashAndCashEquivalentsDisclosure: this.cashAndCashEquivalentsDisclosureFg.value,
              billsExchangeChequesDisclosure: this.billsExchangeChequesDisclosureFg.value,
              cashAndCashEquivalentsUnavailableExplanation: this.cashAndCashEquivalentsUnavailableExplanationFg.value,
              cashAndCashEquivalentsNoLegalOwnershipDescription: this.cashAndCashEquivalentsNoLegalOwnershipDescriptionFg.value,
            }
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogicService.getCalculation(this.jrEntity)

    setTimeout(() => {
      this.showCustomControls = true;
      this.cdRef.detectChanges();
    });
  }
}
